import { MembershipDetailed } from 'memberships/schema'
import React, { useEffect, useState } from 'react'
import { DateFormats, formatISOString } from 'utils/dates'
import InfoRow, { InfoRowContainer } from 'uiComponents/infoRow'
import Section from '../../common/section/section'
import { KebabMenu } from 'uiComponents/menus/kebabMenu'
import { useParams } from 'react-router-dom'
import { BaseRouteParams } from 'hocs'
import CommentField, { Comment } from 'uiComponents/comment/comment'
import { useUpdateMembershipMutation } from 'memberships/reduxQueries'
import { usePermission } from 'admin/hocs'
import { LoaderWrapper } from 'uiComponents/loaders'
import Badge, { BadgeVariant } from 'uiComponents/badge/badge'
import { useTranslation } from 'translations'
import './general.scss'

interface GeneralSectionProps {
    membership?: MembershipDetailed
    isLoading?: boolean
    isLoadingMembershipStatus?: boolean
    openCancelMembershipModal: () => void
}

const GeneralSection = ({
    membership,
    isLoading,
    isLoadingMembershipStatus,
    openCancelMembershipModal,
}: GeneralSectionProps) => {
    const { t } = useTranslation()
    const { accountSlug } = useParams<BaseRouteParams>()
    const [updateMembership] = useUpdateMembershipMutation()
    const [comment, setComment] = useState<Comment | undefined>()
    const { hasPermission } = usePermission()

    let membershipStatusText,
        memberSinceText,
        currentValidPeriodText = ''
    let membershipStatusVariant: BadgeVariant | undefined

    if (membership) {
        const membershipExpiryDate = formatISOString(membership.expiresAt, DateFormats.SHORT_DATE)
        if (membership.status === 'inactive') {
            membershipStatusText = t('memberships.membershipDetailsPanel.general.expiredOn', { date: membershipExpiryDate })
            membershipStatusVariant = 'error'
        } else if (membership.autoRenewEnabled) {
            membershipStatusText = t('memberships.membershipDetailsPanel.general.renewsOn', { date: membershipExpiryDate })
            membershipStatusVariant = 'success'
        } else {
            membershipStatusText = t('memberships.membershipDetailsPanel.general.expiresOn', { date: membershipExpiryDate })
            membershipStatusVariant = 'warning'
        }

        memberSinceText = formatISOString(membership.createdAt, DateFormats.EXTRA_LONG_DATE)

        currentValidPeriodText = `${formatISOString(
            membership.createdAt,
            DateFormats.EXTRA_LONG_DATE,
        )} - ${formatISOString(membership.expiresAt, DateFormats.EXTRA_LONG_DATE)}`
    }

    const menuOptions = [
        ...(hasPermission('refund_orders') && membership?.status !== 'inactive'
            ? [
                  {
                      label: t('memberships.membershipDetailsPanel.general.cancelMembership'),
                      onClick: openCancelMembershipModal,
                  },
              ]
            : []),
        {
            label: t('memberships.membershipDetailsPanel.general.goToOrder'),
            link: `/account/${accountSlug}/orders/${membership?.orderUuid}/details`,
        },
        {
            label: t('memberships.membershipDetailsPanel.general.goToProduct'),
            link: `/account/${accountSlug}/products/crud/${membership?.article.uuid}`,
        },
    ]

    const handleSetComment = async (content: string) => {
        if (!accountSlug || !membership) return

        setComment({
            content,
            uuid: comment?.uuid || undefined,
        })

        await updateMembership({
            accountSlug,
            uuid: membership.uuid,
            overrides: {
                comment: {
                    content,
                    uuid: comment?.uuid || undefined,
                },
            },
        })
    }

    useEffect(() => {
        if (membership) {
            setComment(membership.comments[0])
        }
    }, [membership])

    return (
        <Section className="general">
            <div className="action-row">
                <Badge
                    className="renewal-status-badge"
                    variant={membershipStatusVariant}
                    loading={isLoadingMembershipStatus}
                >
                    {membershipStatusText}
                </Badge>
                <KebabMenu options={menuOptions} disabled={isLoading} listAnchor="right" />
            </div>
            <InfoRowContainer loading={isLoading}>
                <InfoRow label={t('memberships.membershipDetailsPanel.general.membershipId')} className="membership-id" value={membership?.membershipId} copyable />
                <InfoRow label={t('memberships.membershipDetailsPanel.general.barcode')} className="barcode" value={membership?.barcode} copyable />
                <InfoRow label={t('memberships.membershipDetailsPanel.general.memberSince')} className="member-since" value={memberSinceText} />
                <InfoRow label={t('memberships.membershipDetailsPanel.general.currentValidPeriod')} className="current-valid-period" value={currentValidPeriodText} />
            </InfoRowContainer>
            <div className="notes-container">
                <LoaderWrapper loading={isLoading}>
                    <CommentField comment={comment} isFetching={false} onHandleSetComment={handleSetComment} />
                </LoaderWrapper>
            </div>
        </Section>
    )
}

export default GeneralSection
